const EMPTY_OBJECT_SCHEMA = Object.freeze({
  type: 'object',
  properties: {},
  required: []
})

export const AI_TOOL_CATALOG = Object.freeze([
  {
    name: 'list_characters',
    kind: 'read',
    execution: 'server',
    description: "List the current user's characters with ids and names.",
    parameters: EMPTY_OBJECT_SCHEMA
  },
  {
    name: 'get_character_sections',
    kind: 'read',
    execution: 'server',
    description:
      'Get specific parts of a character (background, personality, equipped items, inventory, actions, defenses, traits, spells summary, notes). You MUST provide characterId or name.',
    parameters: {
      type: 'object',
      properties: {
        characterId: {
          type: 'string',
          description: 'Exact character id (from list_characters)'
        },
        name: {
          type: 'string',
          description: 'Character name to look up (case-insensitive).'
        },
        sections: {
          type: 'array',
          description: 'Sections to return; defaults to a common overview set.',
          items: {
            type: 'string',
            enum: [
              'background',
              'personality',
              'equipped',
              'inventory',
              'actions',
              'defenses',
              'traits',
              'spells',
              'notes'
            ]
          }
        }
      },
      required: []
    }
  },
  {
    name: 'find_notes',
    kind: 'read',
    execution: 'server',
    description:
      'Find notes by text match in title or content across campaign notes and KnowledgeBase vaults. You MUST provide a query string.',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search text (required)' },
        vaultName: {
          type: 'string',
          description: 'Optional vault name to scope note search'
        },
        source: {
          type: 'string',
          description: 'campaign|knowledgebase'
        },
        limit: { type: 'number', description: 'Max results (default 5)' }
      },
      required: ['query']
    }
  },
  {
    name: 'create_note',
    kind: 'write',
    execution: 'client_confirmed',
    description:
      'Create a new note in the current campaign, or in the knowledge base when no campaign is active. Supports targeting a specific vault by id or name.',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Note title' },
        content: { type: 'string', description: 'Full note content' },
        vaultId: { type: 'string', description: 'Knowledge Base vault id when targeting KB' },
        vaultName: {
          type: 'string',
          description: 'Case-insensitive vault name to target a KB vault'
        },
        visibility: {
          type: 'string',
          description: 'private|campaign|public|user_specific',
          default: 'campaign'
        },
        specificUsers: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional explicit user ids when visibility=user_specific'
        }
      },
      required: ['title', 'content']
    }
  },
  {
    name: 'update_note',
    kind: 'write',
    execution: 'client_confirmed',
    description:
      'Update an existing note by id. For KnowledgeBase notes, include vaultId or set source="knowledgebase". This is a write action and may require client confirmation.',
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Note document id' },
        query: {
          type: 'string',
          description: 'Optional fuzzy note title/text to resolve the target note'
        },
        noteName: {
          type: 'string',
          description: 'Optional note title to resolve when id is missing'
        },
        vaultId: { type: 'string', description: 'Vault id (for KnowledgeBase notes)' },
        vaultName: {
          type: 'string',
          description: 'Optional KnowledgeBase vault name to scope note resolution'
        },
        source: {
          type: 'string',
          description: 'campaign|knowledgebase - helps locate the note'
        },
        title: { type: 'string', description: 'Optional new title' },
        content: {
          type: 'string',
          description: 'When mode=replace, full content; otherwise appended'
        },
        append: { type: 'string', description: 'Preferred: text to append to existing content' },
        mode: {
          type: 'string',
          enum: ['append', 'replace'],
          description: 'Default: append'
        }
      },
      required: []
    }
  },
  {
    name: 'search_spells',
    kind: 'read',
    execution: 'server',
    description:
      'Search spells from the game rules database by name. Returns matching spell summaries. Use get_entity with entityType="spell" and the resolved index to fetch full details.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Spell name or partial name to search for (e.g. "fireball", "cure", "dispel")'
        },
        gameVersion: {
          type: 'string',
          description: 'Game version code: "5e" for D&D 5e (default), "3.5e", etc.'
        },
        level: {
          type: 'number',
          description: 'Optional: filter by spell level (0 for cantrips, 1-9 for levelled spells)'
        },
        limit: { type: 'number', description: 'Max results (default 10)' }
      },
      required: ['query']
    }
  },
  {
    name: 'list_spells',
    kind: 'read',
    execution: 'server',
    description:
      'List spells filtered by level and/or class. Returns a formatted summary. Useful for browsing available spells without a specific search query.',
    parameters: {
      type: 'object',
      properties: {
        level: {
          type: 'number',
          description: 'Spell level to filter (0 for cantrips, 1-9 for levelled spells)'
        },
        className: {
          type: 'string',
          description: 'Optional class filter (e.g. "wizard", "cleric")'
        },
        gameVersion: {
          type: 'string',
          description: 'Game version code: "5e" for D&D 5e (default)'
        },
        limit: { type: 'number', description: 'Max results (default 20, max 50)' }
      },
      required: []
    }
  },
  {
    name: 'search_monsters',
    kind: 'read',
    execution: 'server',
    description:
      'Search monsters from the game rules database by name. Returns matching monster summaries. Use get_entity with entityType="monster" and the resolved index to fetch full details.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Monster name or partial name to search for (e.g. "goblin", "dragon", "lich")'
        },
        gameVersion: {
          type: 'string',
          description: 'Game version code: "5e" for D&D 5e (default), "3.5e", etc.'
        },
        limit: { type: 'number', description: 'Max results (default 10)' }
      },
      required: ['query']
    }
  },
  {
    name: 'list_monsters',
    kind: 'read',
    execution: 'server',
    description:
      'List monsters filtered by challenge rating and/or type. Returns a formatted summary. Useful for browsing available monsters.',
    parameters: {
      type: 'object',
      properties: {
        cr: {
          type: 'number',
          description: 'Challenge rating to filter (e.g. 1, 5, 10)'
        },
        type: {
          type: 'string',
          description: 'Monster type to filter by (e.g. "dragon", "undead")'
        },
        gameVersion: {
          type: 'string',
          description: 'Game version code: "5e" for D&D 5e (default)'
        },
        limit: { type: 'number', description: 'Max results (default 50, max 200)' }
      },
      required: []
    }
  },
  {
    name: 'search_classes',
    kind: 'read',
    execution: 'server',
    description:
      'Search classes from the game rules database by name. Returns matching class summaries. Use get_entity with entityType="class" and the resolved index to fetch full details.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Class name or partial name to search for (e.g. "wizard", "fighter", "cleric")'
        },
        gameVersion: {
          type: 'string',
          description: 'Game version code: "5e" for D&D 5e (default), "3.5e", etc.'
        },
        limit: { type: 'number', description: 'Max results (default 10)' }
      },
      required: ['query']
    }
  },
  {
    name: 'list_classes',
    kind: 'read',
    execution: 'server',
    description:
      'List all classes for a given game version. Returns a formatted summary. Useful for browsing available classes.',
    parameters: {
      type: 'object',
      properties: {
        gameVersion: {
          type: 'string',
          description: 'Game version code: "5e" for D&D 5e (default)'
        },
        limit: { type: 'number', description: 'Max results (default 20, max 50)' }
      },
      required: []
    }
  },
  {
    name: 'resolve_entity',
    kind: 'read',
    execution: 'server',
    description:
      'Resolve a fuzzy or misspelled user query to the most likely monster, spell, class, note, vault, or character before running get_entity or get_entity_media.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'The entity name or phrase to resolve.'
        },
        preferredTypes: {
          type: 'array',
          description:
            'Optional entity types to constrain matching. Use monster, spell, class, note, vault, or character.',
          items: {
            type: 'string',
            enum: ['monster', 'spell', 'class', 'note', 'vault', 'character']
          }
        },
        vaultName: {
          type: 'string',
          description: 'Optional vault name when resolving notes or vaults.'
        },
        gameVersion: {
          type: 'string',
          description: 'Optional rules version for monster, spell, and class resolution. Defaults to 5e.'
        },
        limit: {
          type: 'number',
          description: 'Max candidates to return. Defaults to 5.'
        },
        queryKind: {
          type: 'string',
          description: 'How to treat the query: exact entity, category/family, or broad lore.',
          enum: ['exact', 'category', 'broad_lore', 'unknown']
        }
      },
      required: ['query']
    }
  },
  {
    name: 'get_entity',
    kind: 'read',
    execution: 'server',
    description:
      'Fetch a resolved entity through one canonical interface. This deterministic tool expects a concrete entity type plus id/index/name and preserves gameVersion for rules entities.',
    parameters: {
      type: 'object',
      properties: {
        entityType: {
          type: 'string',
          description: 'Resolved entity type.',
          enum: ['monster', 'spell', 'class', 'note', 'vault', 'character']
        },
        id: {
          type: 'string',
          description: 'Resolved entity id for note, vault, character, or as a generic fallback.'
        },
        index: {
          type: 'string',
          description: 'Resolved rules index for monster, spell, or class.'
        },
        name: {
          type: 'string',
          description: 'Optional resolved display name for entities that can still be fetched deterministically by name.'
        },
        source: {
          type: 'string',
          description: 'Optional source namespace for note-style entities.'
        },
        sourceId: {
          type: 'string',
          description: 'Optional exact source path for notes.'
        },
        vaultId: {
          type: 'string',
          description: 'Optional vault id for note lookups.'
        },
        vaultName: {
          type: 'string',
          description: 'Optional resolved vault name for vault retrieval.'
        },
        campaignId: {
          type: 'string',
          description: 'Optional campaign id for note lookups.'
        },
        gameVersion: {
          type: 'string',
          description: 'Optional D&D version code for rules entities.'
        }
      },
      required: ['entityType']
    }
  },
  {
    name: 'get_entity_media',
    kind: 'read',
    execution: 'server',
    description:
      'Fetch media for a resolved entity through one canonical interface. This deterministic tool expects a concrete entity type plus id/index/name and preserves gameVersion for rules entities.',
    parameters: {
      type: 'object',
      properties: {
        entityType: {
          type: 'string',
          description: 'Resolved entity type.',
          enum: ['monster', 'spell', 'class', 'note', 'vault', 'character']
        },
        id: {
          type: 'string',
          description: 'Resolved entity id for note, vault, character, or as a generic fallback.'
        },
        index: {
          type: 'string',
          description: 'Resolved rules index for monster, spell, or class.'
        },
        name: {
          type: 'string',
          description: 'Optional resolved display name for entities that can still be fetched deterministically by name.'
        },
        source: {
          type: 'string',
          description: 'Optional source namespace for note-style entities.'
        },
        sourceId: {
          type: 'string',
          description: 'Optional exact source path for notes.'
        },
        vaultId: {
          type: 'string',
          description: 'Optional vault id for note lookups.'
        },
        vaultName: {
          type: 'string',
          description: 'Optional resolved vault name for vault retrieval.'
        },
        campaignId: {
          type: 'string',
          description: 'Optional campaign id for note lookups.'
        },
        gameVersion: {
          type: 'string',
          description: 'Optional D&D version code for rules entities.'
        }
      },
      required: ['entityType']
    }
  },
  {
    name: 'roll_dice',
    kind: 'write',
    execution: 'client_immediate',
    description:
      'Roll tabletop dice using standard notation. You MUST call this for every dice roll and never invent results. Example formulas: 1d20+5, 2d6+3, 1d8, 4d6kh3.',
    parameters: {
      type: 'object',
      properties: {
        formula: {
          type: 'string',
          description: 'Dice notation like 1d20+5, 2d6+3, 1d8, 4d6kh3'
        },
        note: {
          type: 'string',
          description: 'Label for the roll, e.g. "Attack Roll", "Initiative", "Damage"'
        },
        characterId: {
          type: 'string',
          description: 'Optional character id to attribute the roll'
        }
      },
      required: ['formula']
    }
  },
  {
    name: 'open_dice_roller',
    kind: 'read',
    execution: 'client_immediate',
    description: 'Open the dice roller UI panel for the user.',
    parameters: {
      type: 'object',
      properties: {
        characterId: { type: 'string', description: 'Optional character context to preselect' },
        campaignId: { type: 'string', description: 'Optional campaign context' },
        sessionId: { type: 'string', description: 'Optional session context' }
      },
      required: []
    }
  }
])

function cloneValue(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value))
}

export function getAiToolCatalog() {
  return AI_TOOL_CATALOG.map((tool) => cloneValue(tool))
}

export function getAiToolDefinition(name) {
  const needle = String(name || '').trim()
  if (!needle) return null
  const match = AI_TOOL_CATALOG.find(
    (tool) =>
      tool?.name === needle ||
      (Array.isArray(tool?.aliases) && tool.aliases.some((alias) => alias === needle))
  )
  return match ? cloneValue(match) : null
}
